/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable camelcase */
import { useCallback, useRef, useState } from 'react';
import _ from 'lodash';
import { useAppDispatch, useAppSelector } from '../../../../store/hooksTypes';
import { setSBClfState } from '../../../../models/sbDataTableModel/sbDataTableSlice';
import { extractFilterSelected } from '../../../../utils/commonMethods/generalMethodsExtended';
import { loadSbClfData } from '../../../../models/sbDataTableModel/sbDataTableThunk';

type clfColumnsType = {
  Header: string,
  filterType: string,
  id: string,
  loadOptions: () => void,
  storeVariable: Record<string, boolean>,
  filterName: string,
  isLoading: boolean,
  isSortableColumn: boolean,
  selectionHandler?: (selected: Array<string>) => void,
};
export function useBrowserFilterStructure() {
  const browserClfDispatcher = useAppDispatch();
  const {
    browserTableClfState,
    isColumnDataLoading,
    isAtaLoading,
    isModLoading,
    isSbReferenceLoading,
    isSbStatusLoading,
    isSbReportingDateLoading,
    isSbClassificationLoading,
    isSbFunctionLoading,
    isConfigurationNumberLoading,
    isMpReferenceLoading,
    isModTitleLoading,
    isSbTitleLoading,
    isAlertLoading,
    isMpTitleLoading,
  } = useAppSelector((state: any) => state.displaySbDataTable);
  const { sb_filter_selection_configuration } = useAppSelector((state: any) => state.popUpFilter);
  const {
    id_aircraft_registration,
    ata_4d,
    mod_reference,
    sb_reference,
    sb_status,
    sb_reporting_date,
    sb_classification,
    sb_function,
    configuration_number,
    mp_reference,
    mod_title,
    sb_title,
    operator_code_icao,
    mp_title,
    alert,
  } = browserTableClfState;
  const {
    aircraft_type,
    operator_name,
    selected_msn,
  } = sb_filter_selection_configuration;
  const selectedFilters = extractFilterSelected(browserTableClfState);
  const [selectedClfMsns, setSelectedClfMsns] = useState<Array<string>>(selectedFilters.id_aircraft_registration);
  const lastDataSelectionMsn = useRef<Record<string, any>>({});
  const lastClfMsn = useRef<Record<string, any>>({});

  const loadMsnOptions = useCallback(() => {
    const key = 'id_aircraft_registration';
    if (!_.isEqual(lastDataSelectionMsn.current[key], selected_msn)) {
      const msnsAsObject: Record<string, boolean> = selected_msn.reduce((acc: Record<string, boolean>, msn: string) => {
        acc[msn] = false;
        return acc;
      }, {});
      browserClfDispatcher(setSBClfState({
        filterName: 'id_aircraft_registration',
        filterValues: msnsAsObject,
      }));
      lastDataSelectionMsn.current[key] = selected_msn;
    }
  }, [selected_msn, browserClfDispatcher]);

  const fetchFilterDataThroughApi: (
    filterName: string,
    loadingState: string
  ) => void = useCallback((filterName, loadingState) => {
    if (
      !_.isEqual(lastDataSelectionMsn.current[filterName], selected_msn)
      || !_.isEqual(lastClfMsn.current[filterName], selectedClfMsns)
    ) {
      const payload = {
        conditions: {
          aircraft_type,
          id_aircraft_registration: selectedClfMsns.length
            /* istanbul ignore next */
            ? selectedClfMsns
            : selected_msn,
        },
        operator_code_icao: [operator_name],
        distinct: true,
        offset: 0,
        limit: 100000000,
        columns: [filterName],
        orderBy: [filterName],
        isSbStatusToggle: false,
      };

      browserClfDispatcher(
        loadSbClfData(payload)({
          loadingState,
          filterName,
        }),
      );

      lastDataSelectionMsn.current[filterName] = selected_msn;
      lastClfMsn.current[filterName] = selectedClfMsns;
    }
  }, [selected_msn, selectedClfMsns, aircraft_type, operator_name, browserClfDispatcher]);

  const loadAtaOptions = () => {
    fetchFilterDataThroughApi('ata_4d', 'isAtaLoading');
  };

  const loadModOptions = () => {
    fetchFilterDataThroughApi('mod_reference', 'isModLoading');
  };

  const loadSbReferenceOptions = () => {
    fetchFilterDataThroughApi('sb_reference', 'isSbReferenceLoading');
  };

  const loadSbStatusOptions = () => {
    fetchFilterDataThroughApi('sb_status', 'isSbStatusLoading');
  };

  const loadSbReportingDateOptions = () => {
    fetchFilterDataThroughApi('sb_reporting_date', 'isSbReportingDateLoading');
  };

  const loadSbClassificationOptions = () => {
    fetchFilterDataThroughApi('sb_classification', 'isSbClassificationLoading');
  };

  const loadSbFunctionOptions = () => {
    fetchFilterDataThroughApi('sb_function', 'isSbFunctionLoading');
  };

  const loadConfigurationNumberOptions = () => {
    fetchFilterDataThroughApi('configuration_number', 'isConfigurationNumberLoading');
  };

  const loadMpReferenceOptions = () => {
    fetchFilterDataThroughApi('mp_reference', 'isMpReferenceLoading');
  };

  const loadModTitleOptions = () => {
    fetchFilterDataThroughApi('mod_title', 'isModTitleLoading');
  };

  const loadSbTitleOptions = () => {
    fetchFilterDataThroughApi('sb_title', 'isSbTitleLoading');
  };

  const loadMpTitleOptions = () => {
    fetchFilterDataThroughApi('mp_title', 'isMpTitleLoading');
  };

  const loadAlertOptions = () => {
    fetchFilterDataThroughApi('alert', 'isAlertLoading');
  };

  const browserFilterColumns: Array<clfColumnsType> = [
    {
      Header: 'MSN-A/C Registration',
      filterType: 'ACB',
      id: 'id_aircraft_registration',
      loadOptions: loadMsnOptions,
      storeVariable: id_aircraft_registration,
      filterName: 'id_aircraft_registration',
      isLoading: isColumnDataLoading,
      isSortableColumn: true,
      selectionHandler: (selected: any) => {
        setSelectedClfMsns(selected.id_aircraft_registration);
      },
    },
    {
      Header: 'ATA',
      filterType: 'ACB',
      id: 'ata_4d',
      loadOptions: loadAtaOptions,
      storeVariable: ata_4d,
      filterName: 'ata_4d',
      isLoading: isAtaLoading,
      isSortableColumn: true,
    },
    {
      Header: 'MOD',
      filterType: 'ACB',
      id: 'mod_reference',
      loadOptions: loadModOptions,
      storeVariable: mod_reference,
      filterName: 'mod_reference',
      isLoading: isModLoading,
      isSortableColumn: true,
    },
    {
      Header: 'SB Ref.',
      filterType: 'ACB',
      id: 'sb_reference',
      loadOptions: loadSbReferenceOptions,
      storeVariable: sb_reference,
      filterName: 'sb_reference',
      isLoading: isSbReferenceLoading,
      isSortableColumn: true,
    },
    {
      Header: 'Status',
      filterType: 'ACB',
      id: 'sb_status',
      loadOptions: loadSbStatusOptions,
      storeVariable: sb_status,
      filterName: 'sb_status',
      isLoading: isSbStatusLoading,
      isSortableColumn: true,
    },
    {
      Header: 'SB Reporting Date',
      filterType: 'ACB',
      id: 'sb_reporting_date',
      loadOptions: loadSbReportingDateOptions,
      storeVariable: sb_reporting_date,
      filterName: 'sb_reporting_date',
      isLoading: isSbReportingDateLoading,
      isSortableColumn: true,
    },
    {
      Header: 'Compliance',
      filterType: 'ACB',
      id: 'sb_classification',
      loadOptions: loadSbClassificationOptions,
      storeVariable: sb_classification,
      filterName: 'sb_classification',
      isLoading: isSbClassificationLoading,
      isSortableColumn: true,
    },
    {
      Header: 'Nature',
      filterType: 'ACB',
      id: 'sb_function',
      loadOptions: loadSbFunctionOptions,
      storeVariable: sb_function,
      filterName: 'sb_function',
      isLoading: isSbFunctionLoading,
      isSortableColumn: true,
    },
    {
      Header: 'CONF',
      filterType: 'ACB',
      id: 'configuration_number',
      loadOptions: loadConfigurationNumberOptions,
      storeVariable: configuration_number,
      filterName: 'configuration_number',
      isLoading: isConfigurationNumberLoading,
      isSortableColumn: true,
    },
    {
      Header: 'MP',
      filterType: 'ACB',
      id: 'mp_reference',
      loadOptions: loadMpReferenceOptions,
      storeVariable: mp_reference,
      filterName: 'mp_reference',
      isLoading: isMpReferenceLoading,
      isSortableColumn: true,
    },
    {
      Header: 'MOD Title',
      filterType: 'ACB',
      id: 'mod_title',
      loadOptions: loadModTitleOptions,
      storeVariable: mod_title,
      filterName: 'mod_title',
      isLoading: isModTitleLoading,
      isSortableColumn: true,
    },
    {
      Header: 'SB Title',
      filterType: 'ACB',
      id: 'sb_title',
      loadOptions: loadSbTitleOptions,
      storeVariable: sb_title,
      filterName: 'sb_title',
      isLoading: isSbTitleLoading,
      isSortableColumn: true,
    },
    {
      Header: 'Operator',
      filterType: 'ACB',
      id: 'operator_code_icao',
      filterName: 'operator_code_icao',
      isLoading: isColumnDataLoading,
      loadOptions: () => {
        // No API call
      },
      storeVariable: operator_code_icao,
      isSortableColumn: true,
    },
    {
      Header: 'MP Title',
      filterType: 'ACB',
      id: 'mp_title',
      filterName: 'mp_title',
      isLoading: isMpTitleLoading,
      loadOptions: loadMpTitleOptions,
      storeVariable: mp_title,
      isSortableColumn: true,
    },
    {
      Header: 'SB Alert',
      filterType: 'ACB',
      id: 'alert',
      filterName: 'alert',
      isLoading: isAlertLoading,
      loadOptions: loadAlertOptions,
      storeVariable: alert,
      isSortableColumn: true,
    },
  ];

  return browserFilterColumns;
}
